import { Queueable, Job, JobXer, jobsEqual /*, JobX, isJob */ } from "./typedefs/Queuable"
import { NOT_FOUND, Status, success /*, LATER */ } from "./typedefs/RequestStatus";

export type queueCallback = (s: Status, j: Job) => void;

export const noopCallback: queueCallback = (status: Status, j: Job) => { return; }


/* This task queue:

   - stores each task as a list of string, `[functionName, arg1, arg2, ...]`, so that they
     jobs can be easily persisted by subclasses OR postMessage()'d to a worker.
   - presumes each task to be unique, idempotent, and somewhat slow (at least several ms), 
     tolerant of running out of order, and tolerant of being delayed a second or more.
     Consequently, this isn't a queue for high-speed and/or repetitious events.
   - presumes that it will be run on a single thread. There is no mutex protection. The 
     queue is meant to be called asynchronously--multiple microtasks (promises, nextTicks,
     etc.)--can enqueue jobs, but the queue isn't meant for multi-threaded use.

   TaskQueue requires a JobXer (i.e. "Job Executable Factory"), a function that will turn a 
   Job (a string array) into a JobX (i.e. "Job Executable", an async function that returns 
   Promise<Status>). 
   
   It's important that you:
 
   - make sure the jobXer is properly bound, probably by the instance that passes it in
     via `myinstance.myjobxer.bind(this)`), otherwise your jobs will be run with `this` 
     set to the TaskQueue instance, almost certainly what you DON'T want.
 
   - make sure the awaitable function returned by jobXer does the the work when it's run,
     not when it's created. The function needs to do the work later, when the task queue 
     is ready to run it.
*/
export class TaskQueue implements Queueable {

    private queue: Job[] = [];
    private intervalHandle = 0;  // 0 means queue not running, be sure to reset after clearInterval
    private workerMax = 10;
    private workerCount = 0;
    private _onSuccess = noopCallback;
    private _OnFailure = noopCallback;
    private _onEmpty = noopCallback;

    constructor(private jobXer: JobXer) { }

    // IWBNI we could use a setter here, but CRA's Babel setup transpiles them wrong.
    setOnSuccess(handler: queueCallback) {
        this._onSuccess = handler
    }

    setConcurrency(maxWorkers = 10) {
        this.workerMax = maxWorkers
    }

    pending(): Promise<number> {
        return Promise.resolve(this.queue.length);
    }

    peek() {
        return this.queue[this.queue.length - 1];
    }

    enqueue(job: Job) {
        // don't push duplicate jobs
        for (let j of this.queue) if (jobsEqual(job, j)) return false;
        // add the job
        this.queue.unshift(job);
        return true;
    }

    dequeue(): Job | undefined {
        const result = this.queue.pop();
        if (!result) this._onEmpty(0, []);
        return result;
    }

    step(): Promise<Status> {
        let job = this.dequeue();  // will trigger this.onEmpty if no job found
        if (!job) return Promise.resolve(NOT_FOUND);
        return this.jobXer(job)()
            .then(status => {
                if (success(status)) this._onSuccess(status, job as Job)
                else this._OnFailure(status, job as Job)
                return status;
            })
    }

    pause() {
        if (this.intervalHandle) {
            clearInterval(this.intervalHandle)
            this.intervalHandle = 0
        }
        return true;
    }

    start(msBetweenRepriming = 701) { // 701 because primes minimize harmonics
        clearInterval(this.intervalHandle)
        this.intervalHandle = window.setInterval(this.employWorkers.bind(this), msBetweenRepriming)
        setTimeout(this.employWorkers.bind(this))
        return true;
    }

    // fill up all the workers
    private employWorkers() {
        while (this.workerCount <= (this.workerMax - 1)) {
            // reserve a worker and check for a task
            this.workerCount++
            let job = this.dequeue();

            // ...leave if there are no tasks at the moment
            if (!job) {
                this.workerCount--; // cancel worker reservation
                return // no tasks waiting, so leave
            }

            // ...otherwise work the job
            else {
                const jobx = this.jobXer(job)

                // running the task as a promise could flood the microtask queue
                // and potentially interfere with the UI, so instead we make a function
                // that will run the promise and do appropriate recordkeeping
                // const jobThunk = this.employ(jobx, job)
                const jobThunk = () =>
                    jobx()
                        .then(status => success(status)
                            ? this._onSuccess(status, job as Job)
                            : this._OnFailure(status, job as Job)
                        )
                        .catch(e => console.error("error processing job", job, e))
                        .then(() => {
                            // console.debug("deallocating worker", this.workerCount);  // very helpful for debugging the queue
                            this.workerCount--; // free worker
                            setTimeout(this.employWorkers.bind(this))  // refill the queue asap.
                        })

                // ...then we will put it at the end of the event-loop queue
                setTimeout(jobThunk);
            };
        }
    }

}
